import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import { FileUploader } from 'components/FileUploader/FileUploader';
import { FormField } from 'components/FormField/FormField';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type Props<TFormikValues> = {
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  label?: ReactNode;
  multiple?: boolean;
  accept?: string;
  maxSizeInMB?: number;
  confirmRepeatedUploadHeaderMsg?: string;
  onChange?: (value: File[]) => void;
  onClear?: (callback: () => void) => void;
  loading?: boolean;
};

export const FormikUploadField = <TFormikValues,>({
  formik,
  label,
  multiple,
  accept,
  maxSizeInMB,
  confirmRepeatedUploadHeaderMsg,
  onChange,
  ...props
}: Props<TFormikValues>): JSX.Element => {
  const field = formik.getFieldProps(props.name);
  const { error } = formik.getFieldMeta(props.name as string);
  const hasError = fieldHasError<TFormikValues>(formik, props.name);

  const handleChange = (value: File[]) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });

    onChange?.(value);
  };

  return (
    <FormField
      className={classNames(hasError && 'formik-error-scroll-anchor')}
      label={label}
      error={hasError ? error : undefined}
    >
      <FileUploader
        {...field}
        {...props}
        accept={accept}
        multiple={multiple}
        confirmRepeatedUploadHeaderMsg={confirmRepeatedUploadHeaderMsg}
        hasError={hasError}
        onChange={handleChange}
        maxSizeInMB={maxSizeInMB}
      />
    </FormField>
  );
};
